import { getCachedOrgDialToneConfig } from '../features/dial-tones/dial-tone-org-service'
import { Capacitor } from '@capacitor/core'
import { shouldPlayMessageChime } from '../utils/ringer-policy'
import {
  beginChimeAudioSession,
  claimAudioSession,
  endChimeAudioSession,
  getChimeSessionTicket,
  getRingerState,
  isCallActiveNow,
} from './ringer-mode'
import {
  CONNECTING_TONE_URL,
  DIAL_TONE_URL,
  getChimePreference,
  getRingtonePreference,
  IN_CALL_MESSAGE_CHIME_ID,
  resolveChimePlaybackUrl,
  resolveMediaUrl,
  resolveRingtonePlaybackUrl,
  type ChimeId,
  type RingtoneId,
} from '../utils/ringtone-preference'

// ---------------------------------------------------------------------------
// Asset paths (10 sounds + dial tone)
// ---------------------------------------------------------------------------

const RINGTONE_ASSETS = {
  'gentle-ascent': '/sounds/ringtone-gentle-ascent.wav',
  'steady-support': '/sounds/ringtone-steady-support.wav',
  'sunshine-wave': '/sounds/ringtone-sunshine-wave.wav',
  'care-pulse': '/sounds/ringtone-care-pulse.wav',
  'zen-lounge': '/sounds/ringtone-zen-lounge.wav',
} as const satisfies Record<RingtoneId, string>

const CHIME_ASSETS = {
  'ascend-pop': '/sounds/chime-ascend-pop.wav',
  'soft-tap': '/sounds/chime-soft-tap.wav',
  'little-spark': '/sounds/chime-little-spark.wav',
  milestone: '/sounds/chime-milestone.wav',
  'whisper-alert': '/sounds/chime-whisper-alert.wav',
} as const satisfies Record<ChimeId, string>

export type RingtoneType = string
export type ChimeType = string

const RINGTONE_FADE_MS = 8000
const RINGTONE_TARGET_VOLUME = 0.85
const DIAL_TONE_VOLUME = 0.55
const CONNECTING_TONE_VOLUME = 0.5
const CHIME_VOLUME = 0.55
const PREVIEW_RINGTONE_MS = 6000

/**
 * In-call chat alert. Reuses an existing BUILT-IN chime asset (so it is in the
 * autoplay-unlock set below) instead of the user's chosen message chime, so it
 * is short and unobtrusive over live call audio.
 */
const IN_CALL_MESSAGE_CHIME_URL = CHIME_ASSETS[IN_CALL_MESSAGE_CHIME_ID]
/** Deliberately quieter than a normal message chime — it plays mid-conversation. */
const IN_CALL_MESSAGE_CHIME_VOLUME = 0.4

let ringtoneAudio: HTMLAudioElement | null = null
let fadeFrameId: number | null = null
let previewStopTimer: ReturnType<typeof setTimeout> | null = null

function cancelFade(): void {
  if (fadeFrameId !== null) {
    cancelAnimationFrame(fadeFrameId)
    fadeFrameId = null
  }
}

function clearPreviewTimer(): void {
  if (previewStopTimer) {
    clearTimeout(previewStopTimer)
    previewStopTimer = null
  }
}

function ensureRingtoneAudio(): HTMLAudioElement {
  // iOS: a ringtone / dial tone / hold music is about to use the session — it
  // takes ownership from any ambient chime session (cancels the pending chime
  // restore and releases now; no-op unless a chime set it).
  claimAudioSession()
  if (!ringtoneAudio) {
    const el = new Audio()
    el.preload = 'auto'
    el.setAttribute('playsinline', 'true')
    ringtoneAudio = el
  }
  return ringtoneAudio
}

/** Linear volume ramp 0 → target over RINGTONE_FADE_MS. */
function startRingtoneFade(audio: HTMLAudioElement, target = RINGTONE_TARGET_VOLUME): void {
  cancelFade()
  audio.volume = 0
  const start = performance.now()

  const tick = (now: number) => {
    const t = Math.min(1, (now - start) / RINGTONE_FADE_MS)
    audio.volume = target * t
    if (t < 1 && !audio.paused) {
      fadeFrameId = requestAnimationFrame(tick)
    } else {
      fadeFrameId = null
    }
  }

  fadeFrameId = requestAnimationFrame(tick)
}

function resolveRingtoneUrl(type: RingtoneType): string {
  const catalog = getCachedOrgDialToneConfig()?.ringtoneCatalog.slots
  return resolveRingtonePlaybackUrl(type, catalog)
}

function resolveChimeUrl(type: ChimeType): string {
  const catalog = getCachedOrgDialToneConfig()?.chimeCatalog.slots
  return resolveChimePlaybackUrl(type, catalog)
}

function tryPlay(
  audio: HTMLAudioElement,
  attempt = 0,
  onGiveUp?: () => void,
  /** Return false to abandon the remaining retries (then `onGiveUp` runs). */
  shouldRetry?: () => boolean,
): void {
  void audio.play().catch(() => {
    if (attempt < 4 && (shouldRetry?.() ?? true)) {
      window.setTimeout(() => {
        // Re-check at fire time: ownership may have been revoked while waiting.
        if (shouldRetry && !shouldRetry()) {
          onGiveUp?.()
          return
        }
        tryPlay(audio, attempt + 1, onGiveUp, shouldRetry)
      }, 300 * (attempt + 1))
    } else {
      onGiveUp?.()
    }
  })
}

/** Loop incoming ringtone with 8s fade-in. Non-blocking. */
export function playRingtone(type: RingtoneType = getRingtonePreference()): void {
  if (typeof window === 'undefined') return

  const url = resolveRingtoneUrl(type)
  const audio = ensureRingtoneAudio()
  clearPreviewTimer()

  const alreadyPlayingSame = !audio.paused && audio.src === url && audio.loop
  audio.loop = true
  if (audio.src !== url) {
    audio.src = url
    audio.load()
  }

  if (alreadyPlayingSame) return

  startRingtoneFade(audio)
  tryPlay(audio)
}

let dialRepeatHandler: (() => void) | null = null
let dialPlayCount = 0
let dialRepeatLimit = 0

function clearDialRepeatHandler(): void {
  const audio = ringtoneAudio
  if (audio && dialRepeatHandler) {
    audio.removeEventListener('ended', dialRepeatHandler)
  }
  dialRepeatHandler = null
  dialPlayCount = 0
  dialRepeatLimit = 0
}

/** Outgoing dial tone — loops at fixed volume (no fade). Built-in default. */
export function playDialTone(): void {
  playDialToneUrl(resolveMediaUrl(DIAL_TONE_URL), { loop: true })
}

/**
 * Outgoing dial / ringback from a resolved URL.
 * - `loop: true` — built-in continuous ringback
 * - `repeatCount` — play that many full takes then call `onExhausted` (custom org tones)
 */
export function playDialToneUrl(
  url: string,
  options?: { loop?: boolean; repeatCount?: number; onExhausted?: () => void },
): void {
  if (typeof window === 'undefined') return

  const resolved = resolveMediaUrl(url)
  const audio = ensureRingtoneAudio()
  clearPreviewTimer()
  cancelFade()
  clearDialRepeatHandler()

  const loop = options?.loop === true
  const repeatCount = options?.repeatCount ?? 0
  audio.volume = DIAL_TONE_VOLUME

  if (loop || repeatCount <= 0) {
    audio.loop = true
    if (audio.src !== resolved) {
      audio.src = resolved
      audio.load()
    }
    tryPlay(audio)
    return
  }

  audio.loop = false
  dialPlayCount = 0
  dialRepeatLimit = repeatCount
  if (audio.src !== resolved) {
    audio.src = resolved
    audio.load()
  }

  dialRepeatHandler = () => {
    dialPlayCount += 1
    if (dialPlayCount >= dialRepeatLimit) {
      clearDialRepeatHandler()
      options?.onExhausted?.()
      return
    }
    try {
      audio.currentTime = 0
    } catch {
      /* ignore */
    }
    tryPlay(audio)
  }
  audio.addEventListener('ended', dialRepeatHandler)
  tryPlay(audio)
}

// ---------------------------------------------------------------------------
// Connecting-tone playthrough tracking
//
// The connecting tone carries the spoken "calls are recorded" reminder.
// There is NO connect gate (removed with #489 and deliberately NOT restored):
// the call promotes to active on media as usual and the clip plays over the
// first seconds of the call. This tracker only enforces once-per-call playback
// (never replay a completed playthrough) and feeds telemetry/diagnostics. The
// native iOS player reports its start/duration through
// markConnectingToneStarted; the HTML5 element reports via its `playing`
// event + metadata duration.
// ---------------------------------------------------------------------------

/** Bundled /sounds/connecting.wav is 2.4s; used when metadata is unavailable. */
export const DEFAULT_CONNECTING_TONE_DURATION_MS = 2_400

let connectingToneStartedAt: number | null = null
let connectingToneDurationMs: number | null = null
/** Native/HTML5 player reported the disclosure playthrough finished. */
let connectingToneCompleted = false
/** Set while the NATIVE iOS/Android player owns the tone — HTML5 asserts must stand down. */
let nativeConnectingToneActive = false
/**
 * True from {@link beginConnectingTonePlaythrough} until a playthrough is
 * stamped (or the phase ends). Distinguishes "native retries still pending"
 * from "never started" for the assert interval and telemetry.
 */
let connectingToneAwaitingStart = false

/** Begin a connecting-phase disclosure: reset the clock and wait for playback. */
export function beginConnectingTonePlaythrough(): void {
  resetConnectingToneProgress()
  connectingToneAwaitingStart = true
}

export function isConnectingToneAwaitingStart(): boolean {
  return connectingToneAwaitingStart && connectingToneStartedAt === null
}

/** Record that a full connecting-tone playthrough started now (native or HTML5). */
export function markConnectingToneStarted(durationMs?: number | null): void {
  connectingToneAwaitingStart = false
  connectingToneCompleted = false
  connectingToneStartedAt = Date.now()
  connectingToneDurationMs =
    typeof durationMs === 'number' && Number.isFinite(durationMs) && durationMs > 0
      ? durationMs
      : DEFAULT_CONNECTING_TONE_DURATION_MS
}

/**
 * Native AVAudioPlayer / MediaPlayer (or HTML5 `ended`) finished one playthrough.
 * From here nothing may start the reminder again for this call.
 */
export function markConnectingToneCompleted(): void {
  connectingToneAwaitingStart = false
  connectingToneCompleted = true
}

export function isConnectingToneCompletionSignaled(): boolean {
  return connectingToneCompleted
}

/** Forget playthrough progress (new call / tone swapped to a different asset). */
export function resetConnectingToneProgress(): void {
  connectingToneStartedAt = null
  connectingToneDurationMs = null
  connectingToneAwaitingStart = false
  connectingToneCompleted = false
}

/**
 * True once the disclosure has completed one full playthrough. From that
 * moment NOTHING may start it again for this call — the spec is "both parties
 * hear it ONCE, then it stops, then they talk".
 */
export function hasConnectingTonePlaythroughCompleted(now = Date.now()): boolean {
  if (connectingToneCompleted) return true
  if (connectingToneStartedAt === null) return false
  return getConnectingToneRemainingMs(now) <= 0
}

/**
 * Milliseconds of the current connecting-tone playthrough still to go.
 * 0 when the tone never started (never hold a call hostage to audio that
 * cannot play) or has already completed one full pass.
 */
export function getConnectingToneRemainingMs(now = Date.now()): number {
  if (connectingToneCompleted) return 0
  if (connectingToneStartedAt === null) return 0
  const duration = connectingToneDurationMs ?? DEFAULT_CONNECTING_TONE_DURATION_MS
  return Math.max(0, connectingToneStartedAt + duration - now)
}

/** Diagnostic snapshot of playthrough state (telemetry / stall watchdog — NOT a connect gate). */
export function getConnectingToneGateSnapshot(now = Date.now()): {
  remainingMs: number
  awaitingStart: boolean
  startedAtMs: number | null
  durationMs: number | null
  nowMs: number
  completionSignaled: boolean
} {
  return {
    remainingMs: getConnectingToneRemainingMs(now),
    awaitingStart: isConnectingToneAwaitingStart(),
    startedAtMs: connectingToneStartedAt,
    durationMs: connectingToneDurationMs,
    nowMs: now,
    completionSignaled: connectingToneCompleted,
  }
}

export function setNativeConnectingToneActive(active: boolean): void {
  nativeConnectingToneActive = active
}

/**
 * Connecting tone — the mandatory "calls are recorded" disclosure, played
 * through the shared (gesture-unlocked) call-audio element while two-way media
 * is being established (the held "Connecting…" phase). Routing (earpiece for a
 * voice call) is applied by the caller BEFORE this runs (useCallRingtone →
 * prepareNativeRingback), exactly as for the ringback.
 *
 * SPEC (updated 2026-11-03): the reminder plays exactly ONE full playthrough,
 * then STOPS — it never loops, on any platform or role. It never delays or
 * blocks the call: media connects and the call goes active as usual, with the
 * clip finishing over the first seconds of the call. If connecting outlasts
 * the tone, the remainder of the wait is silent.
 */
export function playConnectingTone(url?: string): void {
  if (typeof window === 'undefined') return
  // The native iOS/Android player owns the tone — do not double-play via HTML5.
  if (nativeConnectingToneActive) return
  // One playthrough already done this call — never start it again.
  if (hasConnectingTonePlaythroughCompleted()) return

  const resolved = resolveMediaUrl(url ?? CONNECTING_TONE_URL)
  const audio = ensureRingtoneAudio()
  clearPreviewTimer()
  cancelFade()
  clearDialRepeatHandler()

  // Loading a different asset restarts the disclosure from the top — the
  // playthrough clock must restart with it, and we stay in "awaiting start"
  // until the new asset's `playing` event so the completion tracker cannot
  // sneak through remaining=0 during the swap.
  if (audio.src !== resolved) {
    connectingToneStartedAt = null
    connectingToneDurationMs = null
    connectingToneAwaitingStart = true
    connectingToneCompleted = false
  }

  audio.loop = false
  audio.volume = CONNECTING_TONE_VOLUME
  if (audio.src !== resolved) {
    audio.src = resolved
    audio.load()
  }
  // Stamp the playthrough start the moment audio is ACTUALLY audible (guarded
  // so re-asserts of an already-running playthrough don't move the clock).
  audio.addEventListener(
    'playing',
    () => {
      if (connectingToneStartedAt !== null) return
      markConnectingToneStarted(
        Number.isFinite(audio.duration) && audio.duration > 0
          ? Math.round(audio.duration * 1000)
          : DEFAULT_CONNECTING_TONE_DURATION_MS,
      )
    },
    { once: true },
  )
  audio.addEventListener(
    'ended',
    () => {
      markConnectingToneCompleted()
    },
    { once: true },
  )
  tryPlay(audio)
}

/**
 * Re-assert the connecting tone after CallKit / ConnectionService / Chime
 * reconfigures the native audio session (which often pauses HTML5 Audio).
 * Bounded by the once-then-stop spec: a COMPLETED playthrough is never
 * restarted (the old unbounded re-assert was one of the loop mechanisms).
 * No-op when already playing a non-empty src.
 */
export function assertConnectingTonePlaying(url?: string): void {
  if (typeof window === 'undefined') return
  if (nativeConnectingToneActive) return
  if (hasConnectingTonePlaythroughCompleted()) return
  const audio = ringtoneAudio
  if (audio && !audio.paused && audio.src && audio.volume > 0) return
  playConnectingTone(url)
}

/** Short preview of an arbitrary tone URL (admin Dial Tone settings). */
export function previewToneUrl(url: string, ms = PREVIEW_RINGTONE_MS): void {
  if (typeof window === 'undefined') return
  playDialToneUrl(url, { loop: true })
  previewStopTimer = setTimeout(() => stopRingtone(), ms)
}

/**
 * Loop hold music through the shared (gesture-unlocked) ringtone element.
 *
 * A dedicated `new Audio()` for hold music was blocked by autoplay policy on
 * the HELD party's device — they receive the peer's HOLD signal without any
 * local user gesture, so only the singleton element (already primed by
 * unlockAudioOnGesture / previous ring playback) can start reliably.
 */
export function playHoldMusic(url: string, volume = 0.32): void {
  if (typeof window === 'undefined') return

  const audio = ensureRingtoneAudio()
  clearPreviewTimer()
  cancelFade()

  audio.loop = true
  audio.volume = volume
  if (audio.src !== url) {
    audio.src = url
    audio.load()
  }
  tryPlay(audio)
}

/** Preview ringtone in profile — fade-in then auto-stop after one phrase. */
export function previewRingtone(type: RingtoneType): void {
  playRingtone(type)
  previewStopTimer = setTimeout(() => stopRingtone(), PREVIEW_RINGTONE_MS)
}

/** Single-play message chime. Immediate attack, no loop. */
export function playChime(type: ChimeType = getChimePreference()): void {
  if (typeof window === 'undefined') return

  const audio = new Audio(resolveChimeUrl(type))
  audio.volume = CHIME_VOLUME
  audio.setAttribute('playsinline', 'true')
  playChimeElement(audio)
}

/** Preview chime in profile. */
export function previewChime(type: ChimeType): void {
  playChime(type)
}

/**
 * Play a chime element. On iOS the chime goes through an `.ambient` audio
 * session so the hardware mute switch silences it — but only when that is safe
 * (no call, no other media playing; see ringer-mode.ts), and the session is
 * handed back exactly once when the chime ends, errors, or never plays.
 * Android / web play immediately, unchanged (their Silent/DND gating lives in
 * ringer-policy).
 */
function playChimeElement(audio: HTMLAudioElement): void {
  if (Capacitor.getPlatform() !== 'ios' || isCallActiveNow()) {
    tryPlay(audio)
    return
  }
  let finished = false
  const ticket = getChimeSessionTicket()
  const finish = () => {
    if (finished) return
    finished = true
    endChimeAudioSession(ticket)
  }
  audio.addEventListener('ended', finish, { once: true })
  audio.addEventListener('error', finish, { once: true })
  void beginChimeAudioSession(false).then((result) => {
    if (result === 'cancelled') {
      // A call / ringtone took the audio session while we waited — skip the chime.
      finished = true
      return
    }
    if (result === 'plain') {
      // Session deliberately left alone (other media such as a voice note is
      // playing, or the plugin failed) — nothing to hand back. NOTE: the chime
      // therefore plays on the normal playback session and the mute switch will
      // NOT silence it; that is intended while the user is actively playing
      // other media (switching category would make that media go quiet).
      finished = true
      tryPlay(audio)
      return
    }
    // Ambient chime: if ownership is revoked while play() is still retrying
    // (4 s failsafe, call or ringtone started), drop the chime rather than let
    // it sound later on the default session — which the mute switch wouldn't
    // silence.
    tryPlay(audio, 0, finish, () => getChimeSessionTicket() === ticket)
  })
}

/**
 * Single-play alert for a chat message that arrives DURING an active call.
 *
 * Deliberately reuses the built-in `soft-tap` chime asset (the shortest and
 * least intrusive of the catalogue) rather than the user's chosen message
 * chime: it has to sit UNDER live call audio, and it must be one of the assets
 * primed by {@link unlockAudioOnGesture} so it is audible on iOS.
 *
 * The in-call alert previously built a fresh `AudioContext` per message. A
 * brand-new AudioContext created outside a user gesture stays `suspended` in
 * iOS / the Capacitor WKWebView, so the chime was silent on device. This goes
 * through the same gesture-unlocked HTMLAudio path as {@link playChime}.
 */
export function playInCallMessageChime(): void {
  if (typeof window === 'undefined') return

  // Respect Silent / DND (Android): the alert is a media-stream sound, which
  // the system Silent mode does not mute on its own. iOS call audio session is
  // never switched here (a call is active by definition).
  void getRingerState().then((state) => {
    if (!shouldPlayMessageChime(state)) return
    const audio = new Audio(resolveMediaUrl(IN_CALL_MESSAGE_CHIME_URL))
    audio.volume = IN_CALL_MESSAGE_CHIME_VOLUME
    audio.setAttribute('playsinline', 'true')
    tryPlay(audio)
  })
}

/** Stop looping ringtone / dial tone and reset element. */
export function stopRingtone(): void {
  clearPreviewTimer()
  cancelFade()
  clearDialRepeatHandler()
  const audio = ringtoneAudio
  if (!audio) return
  audio.pause()
  audio.currentTime = 0
  audio.loop = false
  audio.volume = 0
  audio.src = ''
  audio.load()
}

const UNLOCK_URLS = [
  DIAL_TONE_URL,
  CONNECTING_TONE_URL,
  '/sounds/hold-music.wav',
  ...Object.values(RINGTONE_ASSETS),
  ...Object.values(CHIME_ASSETS),
].map(resolveMediaUrl)

/** Prime HTML5 + Web Audio on first user gesture (iOS/Android PWA). */
export function unlockAudioOnGesture(): void {
  if (typeof window === 'undefined') return

  const unlock = () => {
    for (const url of UNLOCK_URLS) {
      const audio = new Audio(url)
      audio.volume = 0.001
      void audio
        .play()
        .then(() => {
          audio.pause()
          audio.src = ''
        })
        .catch(() => {})
    }

    const AudioCtx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
    if (AudioCtx) {
      const ctx = new AudioCtx()
      void ctx.resume().finally(() => void ctx.close())
    }

    window.removeEventListener('pointerdown', unlock)
    window.removeEventListener('keydown', unlock)
    window.removeEventListener('touchstart', unlock)
  }

  window.addEventListener('pointerdown', unlock, { once: true, passive: true })
  window.addEventListener('touchstart', unlock, { once: true, passive: true })
  window.addEventListener('keydown', unlock, { once: true })
}

/** Native shell bootstrap — silent play to unlock autoplay. */
export function unlockNativeAudio(): void {
  if (typeof window === 'undefined') return
  const audio = new Audio(resolveMediaUrl(DIAL_TONE_URL))
  audio.volume = 0.001
  audio.setAttribute('playsinline', 'true')
  void audio
    .play()
    .then(() => {
      audio.pause()
      audio.src = ''
    })
    .catch(() => {})
}
